import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Table,
  Badge,
  ButtonGroup,
  Modal,
  Form,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { api } from 'api';
import { formatDate } from 'common/utils';
import { CURRENCY } from 'common/constants';
import { FaSync, FaCheck, FaTimes, FaMoneyBillWave } from 'react-icons/fa';
import { PiHandWithdrawFill } from 'react-icons/pi';
import { Pagination } from 'components';
import 'pages/withdraw/_withdraw.scss';

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  paid: 'Paid',
  rejected: 'Rejected',
};

const STATUS_VARIANT = {
  pending: 'warning',
  approved: 'info',
  paid: 'success',
  rejected: 'danger',
};

const AdminWithdrawals = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionModal, setActionModal] = useState({ show: false, request: null, action: null });
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: currentPage, page_size: itemsPerPage };
      if (statusFilter) params.status = statusFilter;
      const res = await api.getAdminWithdrawalRequests(params);
      // Support paginated { count, results } or legacy array
      if (res && typeof res === 'object' && Array.isArray(res.results)) {
        setRequests(res.results);
        setTotalCount(Number(res.count) ?? res.results.length);
      } else {
        const list = Array.isArray(res) ? res : [];
        setRequests(list);
        setTotalCount(list.length);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load withdrawal requests.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, currentPage, itemsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (num) => setItemsPerPage(num);

  const openActionModal = (request, action) => {
    setActionModal({ show: true, request, action });
    setAdminNotes('');
  };

  const closeActionModal = () => {
    setActionModal({ show: false, request: null, action: null });
    setAdminNotes('');
  };

  const handleConfirmAction = async () => {
    if (!actionModal.request) return;
    setSubmitting(true);
    try {
      await api.updateWithdrawalRequestStatus(actionModal.request.id, {
        status: actionModal.action,
        admin_notes: adminNotes || undefined,
      });
      const actionLabel = actionModal.action === 'paid' ? 'Marked as paid' : actionModal.action === 'approved' ? 'Approved' : 'Rejected';
      toast.success(`Withdrawal request #${actionModal.request.id} ${actionLabel}.`);
      closeActionModal();
      loadData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update request.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Container fluid className="withdraw-page">
        <Alert variant="danger" className="withdraw-error mt-4">
          {error}
          <Button variant="outline-danger" size="sm" className="ms-2" onClick={loadData}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="withdraw-page">
      <div className="withdraw-header mb-4">
        <h2 className="d-flex align-items-center gap-2">
          <PiHandWithdrawFill />
          Withdrawal requests
        </h2>
        <p className="text-muted mb-0">
          Review and fulfill ground admin withdrawal requests. Approve, reject, or mark as paid after transfer.
        </p>
      </div>

      <Card className="withdraw-filters-card">
        <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={loadData}
              disabled={loading}
            >
              <FaSync className="me-1" />
              Refresh
            </Button>
            <ButtonGroup size="sm">
              <Button
                variant={statusFilter === '' ? 'primary' : 'outline-primary'}
                onClick={() => setStatusFilter('')}
              >
                All
              </Button>
              <Button
                variant={statusFilter === 'pending' ? 'primary' : 'outline-primary'}
                onClick={() => setStatusFilter('pending')}
              >
                Pending
              </Button>
              <Button
                variant={statusFilter === 'approved' ? 'primary' : 'outline-primary'}
                onClick={() => setStatusFilter('approved')}
              >
                Approved
              </Button>
              <Button
                variant={statusFilter === 'paid' ? 'primary' : 'outline-primary'}
                onClick={() => setStatusFilter('paid')}
              >
                Paid
              </Button>
              <Button
                variant={statusFilter === 'rejected' ? 'primary' : 'outline-primary'}
                onClick={() => setStatusFilter('rejected')}
              >
                Rejected
              </Button>
            </ButtonGroup>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="withdraw-loading">
              <Spinner animation="border" role="status" />
              <p>Loading...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="withdraw-empty-requests">
              <p className="mb-0">No withdrawal requests found.</p>
            </div>
          ) : (
            <div className="withdraw-table-container">
              <Table responsive hover className="withdraw-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Requested by</th>
                    <th>Date</th>
                    <th>Net amount</th>
                    <th>Gross / Fee</th>
                    <th>Tx count</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id}>
                      <td>{r.id}</td>
                      <td>
                        <div>{r.requested_by_name || r.requested_by_email}</div>
                        <small className="text-muted">{r.requested_by_email}</small>
                      </td>
                      <td>{formatDate(r.created)}</td>
                      <td>
                        <strong>
                          {CURRENCY} {parseFloat(r.amount).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                        </strong>
                      </td>
                      <td className="small text-muted">
                        {CURRENCY} {parseFloat(r.total_gross).toLocaleString('en-PK', { minimumFractionDigits: 2 })}{' '}
                        / {CURRENCY} {parseFloat(r.platform_fee).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                      </td>
                      <td>{r.items_count}</td>
                      <td>
                        <Badge bg={STATUS_VARIANT[r.status] || 'secondary'}>
                          {STATUS_LABELS[r.status] || r.status}
                        </Badge>
                      </td>
                      <td>
                        {(r.status === 'pending' || r.status === 'approved') && (
                          <div className="withdraw-action-buttons">
                            {r.status === 'pending' && (
                              <>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => openActionModal(r, 'approved')}
                                >
                                  <FaCheck className="me-1" /> Approve
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => openActionModal(r, 'rejected')}
                                >
                                  <FaTimes className="me-1" /> Reject
                                </Button>
                              </>
                            )}
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => openActionModal(r, 'paid')}
                            >
                              <FaMoneyBillWave className="me-1" /> Mark paid
                            </Button>
                          </div>
                        )}
                        {r.admin_notes && (
                          <div className="small text-muted mt-1" title={r.admin_notes}>
                            {r.admin_notes.slice(0, 40)}{r.admin_notes.length > 40 ? '…' : ''}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {totalCount > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalCount}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      <Modal show={actionModal.show} onHide={closeActionModal} centered className="withdraw-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            {actionModal.action === 'approved' && 'Approve withdrawal'}
            {actionModal.action === 'rejected' && 'Reject withdrawal'}
            {actionModal.action === 'paid' && 'Mark as paid'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionModal.request && (
            <p className="mb-3">
              Request #{actionModal.request.id} — {actionModal.request.requested_by_name} —{' '}
              {CURRENCY} {parseFloat(actionModal.request.amount).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
            </p>
          )}
          <Form.Group>
            <Form.Label>Admin notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="e.g. Transfer reference or reason for rejection"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeActionModal}>
            Cancel
          </Button>
          <Button
            variant={
              actionModal.action === 'rejected'
                ? 'danger'
                : actionModal.action === 'paid'
                  ? 'primary'
                  : 'success'
            }
            onClick={handleConfirmAction}
            disabled={submitting}
          >
            {submitting ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminWithdrawals;
